import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBrandData } from './useBrandData';
import BrandAnalysisComponent, { BrandData } from './BrandAnalysisComponent';
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';

export default function BrandAnalysisView() {
  const { id: brandId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { brand, loading: brandLoading, fetchBrandDetails } = useBrandData(brandId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourceImages, setResourceImages] = useState<Array<{ url: string; description?: string; dimensions?: any }>>([]);

  // Fetch resource cache images for authenticated brands
  const fetchResourceImages = async (brandId: string) => {
    try {
      const { data: resourceCache, error } = await supabase
        .from('resource_cache')
        .select('url_to_inject, description, metadata')
        .eq('brand_id', brandId)
        .eq('type', 'image')
        .eq('processed', true)
        .not('url_to_inject', 'is', null);

      if (error) {
        console.error('Error fetching resource images:', error);
        return [];
      }

      return resourceCache?.map(resource => ({
        url: resource.url_to_inject!,
        description: resource.description || undefined,
        dimensions: (resource.metadata as any)?.dimensions || undefined
      })) || [];
    } catch (err) {
      console.error('Error fetching resource images:', err);
      return [];
    }
  };

  useEffect(() => {
    const loadBrand = async () => {
      if (!brandId) {
        setError('No brand ID provided');
        setLoading(false);
        return;
      }

      try {
        const brandData = await fetchBrandDetails(brandId);
        if (!brandData) {
          setError('Brand not found');
        } else {
          // Fetch resource cache images for authenticated brands
          const images = await fetchResourceImages(brandId);
          setResourceImages(images);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading brand:', err);
        setError('Failed to load brand');
        setLoading(false);
      }
    };

    loadBrand();
  }, [brandId, fetchBrandDetails]);

  // Convert the brand data to the format expected by BrandAnalysisComponent
  const convertToBrandData = (): BrandData | null => {
    if (!brand) return null;

    return {
      id: brand.id,
      url: brand.url || undefined,
      brand_name: brand.brand_name || undefined,
      brand_data: {
        brand_name: brand.brand_name || undefined,
        elevator_pitch: brand.elevator_pitch || undefined,
        target_market: brand.target_market || undefined,
        value_props: brand.value_props || undefined,
        differentiators: brand.differentiators || undefined,
        brand_voice: brand.brand_voice || undefined,
        call_to_action: (brand as any).cta || undefined,
        niche: brand.niche || undefined,
        target_geography: brand.target_geography || undefined,
        products_overview: brand.products_overview || undefined,
        b2b_b2c: brand.b2b_b2c || undefined,
      },
      // For authenticated brands, use resource cache images instead of metadata
      metadata: {
        ...(brand as any).metadata,
        images: resourceImages.length > 0 ? resourceImages : undefined,
      },
      scraping_results: (brand as any).web_site_scraping_results || undefined,
      brand_analysis_results: (brand as any).metadata?.brand_analysis_results || undefined,
    };
  };

  const handleComplete = () => {
    navigate(`/brands/${brandId}`);
  };

  if (loading || brandLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <h1 className="text-2xl font-bold mb-2">Loading Brand Analysis</h1>
            <p className="text-muted-foreground">
              Fetching your brand data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Error Loading Brand</h1>
          <p className="text-muted-foreground mb-6">
            {error || 'Failed to load brand data'}
          </p>
          <Button onClick={() => navigate('/brands')}>
            Back to Brands
          </Button>
        </div>
      </div>
    );
  }

  const brandData = convertToBrandData();
  if (!brandData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Incomplete Brand Data</h1>
          <p className="text-muted-foreground mb-6">
            This brand doesn't have enough data for analysis yet.
          </p>
          <Button onClick={() => navigate(`/brands/${brandId}`)}>
            Complete Brand Setup
          </Button>
        </div>
      </div>
    );
  }

  return (
    <BrandAnalysisComponent
      brandData={brandData}
      isAnonymous={false}
      onComplete={handleComplete}
      showClaimButton={false}
    />
  );
}